<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import * as apis from '/@src/client-portal/api'
import {
  ValidacnaSprava,
  ZiadostBasePredlozenieSposobPredlozeniaEnum,
} from '/@src/client-portal'
import { inject, Ref, ref } from 'vue'
import { useSteps } from '/@src/composable/useSteps'
import { NazvyKrokov } from '/@src/utils/zoppm/types'
import { useNotyf } from '/@src/composable/useNotyf'
import slugify from '@sindresorhus/slugify'
import FileDownload from 'js-file-download'

const { t } = useI18n()
const notyf = useNotyf()

const draftApi = new apis.ZoppmDraftApi()
const reportApi = new apis.ZoppmReportApi()
const portalZoppm = new apis.PortalZoppmApi()
const steps = useSteps()
const { setPrevNextStep } = useSteps()

const kodIrz = inject<Ref<string>>('kodIrz')

const errors = ref<Array<ValidacnaSprava>>([])
const sysError = ref<string>()
const previewLoading = ref<boolean>(false)
const submitting = ref<boolean>(false)

const onSubmit = async () => {
  submitting.value = true
  try {
    const response = await draftApi.validateZoppmDraftZoppmDraft(kodIrz!.value)
    if (response.data?.length) {
      errors.value = response.data
      return
    }

    const nowString = new Date().toISOString()
    await portalZoppm
      .submitZoppmPortalZoppm({
        kodIrz: kodIrz!.value,
        predlozenie: {
          sposobPredlozenia: ZiadostBasePredlozenieSposobPredlozeniaEnum.Listinne,
          casOdoslania: nowString,
        },
      })
      .then((res) => {
        localStorage.setItem('zoppmId', res?.data?.zoppmId!)
      })

    steps.setStep(NazvyKrokov['sposobPodaniaZiadostiNaPosudenie'])
    submitting.value = false
  } catch (err: any) {
    console.trace(err)
    notyf.error(t('globalne.chybaNacitaniaDat'))
    submitting.value = false
  }
}
const resetPage = () => {
  errors.value = []
  sysError.value = undefined
}

const onPreview = async () => {
  try {
    previewLoading.value = true
    resetPage()

    const response = await reportApi.getZoppmPdfReportDraftZoppmReport(kodIrz!.value, {
      responseType: 'blob',
    })

    if (response) {
      FileDownload(
        response.data,
        `Ziadost_${slugify(kodIrz?.value || response.headers.filename)}.pdf`
      )
    }
  } catch (error: any) {
    console.error(error)
    sysError.value = ' Vyskytla sa chyba!'
  } finally {
    previewLoading.value = false
  }
}
</script>

<template>
  <IDSKMessage>
    {{ t('zoppm.step16.warning') }}
  </IDSKMessage>
  <IDSKMessage v-if="!!errors.length" error> Žiadosť je v neplatnom stave! </IDSKMessage>
  <IDSKMessage v-if="!!sysError" error>{{ sysError }} </IDSKMessage>
  <IDSKButton
    type="button"
    variant="secondary"
    :disabled="previewLoading"
    class="mb-8"
    :data-test-id="`zoppm-finalizacia-nahlad`"
    @click.prevent="onPreview"
  >
    {{ t('zoppm.step16.btn.preview') }}
    <LoadingSpinner v-show="previewLoading" />
  </IDSKButton>
  <IDSKButtonGroup>
    <IDSKButton
      type="button"
      variant="primary"
      :disabled="!!errors.length || submitting"
      :data-test-id="`zoppm-finalizacia-zaevidovat`"
      @click.prevent="onSubmit"
    >
      {{ t('zoppm.step16.btn.submit') }}
    </IDSKButton>

    <IDSKLink href="#" @click.prevent="() => setPrevNextStep(-1)">
      {{ t('zoppm.step16.btn.back') }}
    </IDSKLink>
  </IDSKButtonGroup>
</template>
